import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import { Users, CalendarClock, Loader2, DollarSign, Archive, ArchiveRestore, Coins } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useError } from '../../../context/ErrorContext';
import PaginationControls from '../../../components/common/PaginationControls';
import ExportExcelButton from '../../../components/common/ExportExcelButton';
import ExportModal, { ExportField } from '../../../components/common/ExportModal';
import { clientsApi, UIClient } from '../../../api/clientsApi';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import RegisterVisitModal from '../components/RegisterVisitModal';
import { NoAcumulaChip } from '../components/AccrualBlockModal';
import { useAccrualBlockMap } from '../../../api/accrualBlocksApi';
import PageHeader from '../../../components/ui/PageHeader';
import SearchInput from '../../../components/common/SearchInput';
import { formatPhoneNumber } from '../../../utils/phoneUtils'; 

// --- INTERFACES ---
interface WhatsAppClient {
    id: string;
    phoneNumber: string;
    visitorName: string | null;
    email: string | null;
    points: number;
    createdAt: Date | string | null;
    lastVisit: Date | null | string;
    countryCode: string;
    gender: string;
    businessId: string;
    birthDay: number | null;
    birthMonth: number | null;
    lastVisitBranchId?: string;
    lastVisitBranchName?: string;
    registrationBranchId?: string;
    registrationBranchName?: string;
    visitedBranchIds?: string[];
    updatedAt?: Date | string | null;
    tags?: string[];   // vista previa del cashback especial por etiqueta (17-oct-2026)
}

const ITEMS_PER_PAGE = 20;

// --- HELPERS ---

const formatDate = (date: Date | null | string): string => {
    if (!date) return 'N/A';
    try {
        let dateObj: Date;
        if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'string') {
            dateObj = new Date(date);
        } else if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
             dateObj = (date as any).toDate();
         } else {
             return 'Fecha inválida';
         }

        if (isNaN(dateObj.getTime())) return 'Fecha inválida';

        return new Intl.DateTimeFormat('es-ES', {
             day: '2-digit',
             month: '2-digit',
             year: 'numeric'
         }).format(dateObj);
    } catch(e) {
         console.error("Error formatting date in ClientList:", e, date);
        return 'Fecha inválida';
    }
};

// Mapea el UIClient del adaptador (lealify-base) al shape que consume esta página.
// base no separa género ni guarda sucursal por contacto en el listado → gaps marcados.
const mapUiToClient = (u: UIClient): WhatsAppClient => ({
    id: u.id,
    phoneNumber: u.phoneNumber || '',
    visitorName: u.visitorName || null,
    email: u.email || null,
    points: typeof u.points === 'number' ? u.points : 0,
    createdAt: u.createdAt || null,
    lastVisit: u.lastVisit || null,
    countryCode: u.countryCode || '52',
    gender: u.gender || '',          // base aún no captura género
    businessId: '',
    birthDay: u.birthDay,
    birthMonth: u.birthMonth,
    registrationBranchName: undefined, // base no expone sucursal de registro en el listado
    tags: u.tags || [],
});

/** Formatea género para exportación */
const formatGender = (gender: string | null | undefined): string => {
    if (!gender) return '-';
    if (gender === 'male') return 'Masculino';
    if (gender === 'female') return 'Femenino';
    return gender;
};

/** Formatea cumpleaños (día/mes) para exportación */
const formatBirthday = (day: number | null | undefined, month: number | null | undefined): string => {
    if (day == null || month == null) return '-';
    const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${day} ${months[month - 1] || month}`;
};

// ========================================================================
// DEFINICIÓN DE CAMPOS EXPORTABLES
// ========================================================================

const buildExportFields = (pointsHeader: string): ExportField[] => [
    { key: 'name',     header: 'Cliente',              label: 'Nombre del cliente',   alwaysIncluded: true,  columnWidth: 30 },
    { key: 'phone',    header: 'Teléfono',             label: 'Teléfono',             alwaysIncluded: true,  columnWidth: 20 },
    { key: 'country',  header: 'País',                 label: 'País (código)',         defaultSelected: true, columnWidth: 10 },
    { key: 'points',   header: pointsHeader,           label: pointsHeader,           alwaysIncluded: true,  columnWidth: 15 },
    { key: 'lastVisit',header: 'Última Visita',        label: 'Última visita',        defaultSelected: true, columnWidth: 18 },
    { key: 'created',  header: 'Registrado',           label: 'Fecha de registro',    defaultSelected: true, columnWidth: 18 },
    { key: 'email',    header: 'Email',                label: 'Correo electrónico',   defaultSelected: true, columnWidth: 28 },
    { key: 'gender',   header: 'Género',               label: 'Género',               defaultSelected: true, columnWidth: 14 },
    { key: 'birthday', header: 'Cumpleaños',           label: 'Cumpleaños',           defaultSelected: true, columnWidth: 14 },
    { key: 'branch',   header: 'Sucursal de Registro', label: 'Sucursal de registro', defaultSelected: true, columnWidth: 22 },
];

/** Mapa de extracción: dado un key, retorna el valor de celda para un cliente */
const getFieldValue = (key: string, client: WhatsAppClient, loyaltyMode: string): string => {
    switch (key) {
        case 'name':      return client.visitorName || 'Sin nombre';
        case 'phone':     return formatPhoneNumber(client.countryCode, client.phoneNumber);
        case 'country':   return client.countryCode || '-';
        case 'points':    return loyaltyMode === 'cashback'
                              ? (client.points ?? 0).toFixed(2)
                              : Math.floor(client.points ?? 0).toString();
        case 'lastVisit': return formatDate(client.lastVisit);
        case 'created':   return formatDate(client.createdAt);
        case 'email':     return client.email || '-';
        case 'gender':    return formatGender(client.gender);
        case 'birthday':  return formatBirthday(client.birthDay, client.birthMonth);
        case 'branch':    return client.registrationBranchName || '-';
        default:          return '-';
    }
};

// --- MAIN COMPONENT ---

const ClientsListPage: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const noAcumula = useAccrualBlockMap();   // «Quién no acumula» (19-oct-2026): chip junto al nombre
    const { addError, clearErrors } = useError();
    const contentTopRef = useRef<HTMLDivElement>(null);

    // --- State ---
    const [clients, setClients] = useState<WhatsAppClient[]>([]);
    // Acción rápida: registrar visita (venta) sin entrar a la ficha
    const [visitFor, setVisitFor] = useState<WhatsAppClient | null>(null);
    const [loading, setLoading] = useState(true);
    const [pageChanging, setPageChanging] = useState(false);
    const [currentPageNum, setCurrentPageNum] = useState(1);
    const [totalHits, setTotalHits] = useState(0);
    
    // Estado para el término de búsqueda actual (necesario para exportar filtrado)
    const [currentSearchTerm, setCurrentSearchTerm] = useState(""); 

    // Estado del modal de exportación
    const [showExportModal, setShowExportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // v1.8.0: ver clientes archivados (soft-delete). false = solo activos; true = solo archivados.
    const [showArchived, setShowArchived] = useState(false);

    // Orden de la lista principal. Default alfabético (la recencia ya la cubren las páginas
    // "Últimos registros" / "Últimas visitas"). El backend /contacts acepta ?sort=.
    const [sortBy, setSortBy] = useState<'name' | 'last_visit' | 'recent'>('name');

    // 1. DETECCIÓN DE MODO DE LEALTAD
    const loyaltyMode = currentUser?.loyaltyMode || 'visits'; 

    // Campos de exportación (memoizados según loyaltyMode)
    const exportFields = React.useMemo(
        () => buildExportFields(loyaltyMode === 'cashback' ? 'Saldo ($)' : 'Puntos'),
        [loyaltyMode]
    );

    // --- Fetch logic ---
    const fetchClientsFromElasticsearch = useCallback(async (
        term: string = "",
        page: number = 1,
        archivedMode: 'exclude' | 'only' | 'all' = (showArchived ? 'only' : 'exclude'),
        sort: 'name' | 'last_visit' | 'recent' = sortBy
    ) => {
        if (!currentUser?.businessId) {
            setClients([]); setTotalHits(0); setLoading(false); return;
        }

        const isPageChange = currentPageNum !== page && clients.length > 0;
        
        if (isPageChange) setPageChanging(true);
        else setLoading(true);
        
        if(clearErrors) clearErrors();
        setCurrentSearchTerm(term); // Guardamos el término actual

        try {
            const result = await clientsApi.list({
                q: term,
                page,
                size: ITEMS_PER_PAGE,
                archived: archivedMode,
                sort,
            });

            setTotalHits(result.total ?? 0);
            setCurrentPageNum(page);

            const mappedClients = result.clients.map(mapUiToClient);

            if (isPageChange) {
                setTimeout(() => {
                    setClients(mappedClients);
                    setPageChanging(false);
                }, 200);
            } else {
                setClients(mappedClients);
                setLoading(false);
            }
        } catch (error: any) {
            console.error("Error during Elasticsearch fetch:", error);
            if(addError) addError(`Error al obtener clientes: ${error.message}`, undefined, "esFetch");
            setClients([]); setTotalHits(0); setCurrentPageNum(1); setPageChanging(false);
        } finally {
            setLoading(false);
        }
    }, [currentUser?.businessId, addError, clearErrors, currentPageNum, clients.length, showArchived, sortBy]);

    // v1.8.0: alternar entre activos y archivados (refetch explícito a página 1)
    const toggleShowArchived = useCallback(() => {
        const next = !showArchived;
        setShowArchived(next);
        setCurrentPageNum(1);
        fetchClientsFromElasticsearch(currentSearchTerm, 1, next ? 'only' : 'exclude');
    }, [showArchived, currentSearchTerm, fetchClientsFromElasticsearch]);

    useEffect(() => {
        if (currentUser?.businessId) {
            if(clearErrors) clearErrors();
            fetchClientsFromElasticsearch('', 1).catch(error => {
                console.error("UNEXPECTED ERROR:", error);
            });
        } else {
            setLoading(false); setClients([]); setTotalHits(0);
        }
    }, [currentUser?.businessId]);

    useEffect(() => {
        if (contentTopRef.current) {
            contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentPageNum]);

    // --- Pagination ---
     const handleNextPage = useCallback(() => {
        if (loading || pageChanging) return;
        const canGoNextEs = currentPageNum * ITEMS_PER_PAGE < totalHits;
        if (canGoNextEs) {
            fetchClientsFromElasticsearch(currentSearchTerm, currentPageNum + 1);
        }
    }, [loading, pageChanging, currentPageNum, totalHits, currentSearchTerm, fetchClientsFromElasticsearch]);

    const handlePrevPage = useCallback(() => {
        if (loading || pageChanging) return;
        if (currentPageNum > 1) {
            fetchClientsFromElasticsearch(currentSearchTerm, currentPageNum - 1);
        }
    }, [loading, pageChanging, currentPageNum, currentSearchTerm, fetchClientsFromElasticsearch]);

    const handleClientSearch = (term: string | null) => {
        fetchClientsFromElasticsearch(term || '', 1);
    };

    const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const next = e.target.value as 'name' | 'last_visit' | 'recent';
        setSortBy(next);
        setCurrentPageNum(1);
        fetchClientsFromElasticsearch(currentSearchTerm, 1, showArchived ? 'only' : 'exclude', next);
    }, [currentSearchTerm, showArchived, fetchClientsFromElasticsearch]);

    // ✅ EXPORTACIÓN CON SELECCIÓN DE CAMPOS
    const exportToExcel = useCallback(async (selectedKeys: string[]) => {
        if (!currentUser?.businessId) return;
        
        setIsExporting(true);

        try {
            const limit = Math.min(5000, totalHits || 5000);

            const response = await clientsApi.list({
                q: currentSearchTerm,
                page: 1,
                size: limit,
                archived: showArchived ? 'only' : 'exclude',
                sort: sortBy,
            });

            const allClients = response.clients.map(mapUiToClient);

            if (allClients.length === 0) {
                if(addError) addError("No hay datos para exportar.", "warning", "exporting");
                return;
            }

            // Construir headers y anchos dinámicamente según campos seleccionados
            const selectedFields = exportFields.filter(f => selectedKeys.includes(f.key));
            const headers = selectedFields.map(f => f.header);
            const colWidths = selectedFields.map(f => ({ wch: f.columnWidth || 15 }));

            // Generar filas dinámicamente
            const data = allClients.map(client =>
                selectedFields.map(f => getFieldValue(f.key, client as WhatsAppClient, loyaltyMode))
            );

            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            ws['!cols'] = colWidths;
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
            const date = new Date().toISOString().split('T')[0];
            const fileName = `clientes_${currentUser?.businessName || 'negocio'}_${date}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (exportError: any) {
            console.error("Export error:", exportError);
            if(addError) addError("No se pudo generar el archivo Excel.", undefined, 'exportExcel', exportError);
        } finally {
            setIsExporting(false);
            setShowExportModal(false);
        }
    }, [currentUser?.businessId, currentUser?.businessName, currentSearchTerm, totalHits, loyaltyMode, exportFields, addError, sortBy]);

    const canGoPrev = currentPageNum > 1;
    const canGoNext = currentPageNum * ITEMS_PER_PAGE < totalHits;

    return (
        <Layout>
            <div className="space-y-6 px-2 sm:px-0">
                <div ref={contentTopRef} id="content-top"></div>
            
                {/* Header */}
                <PageHeader
                    icon={Users}
                    title="Lista de Clientes"
                    subtitle="Gestiona los clientes registrados"
                    className="animate-fade-in-up"
                    style={{animationDelay: '0.1s'}}
                    actions={
                        <ExportExcelButton
                            onExport={() => setShowExportModal(true)}
                            disabled={clients.length === 0 || loading || pageChanging}
                            filename={`clientes_${currentUser?.businessName || 'negocio'}_${new Date().toISOString().split('T')[0]}.xlsx`}
                            className="flex-1 sm:flex-none animate-fade-in-up"
                            style={{animationDelay: '0.15s'}}
                        />
                    }
                />

                {/* Content */}
                <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                    <div className="px-4 py-5 sm:p-6">
                        <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
                            <div className="flex-1">
                                <SearchInput
                                    onSearch={handleClientSearch}
                                    placeholder="Buscar cliente por nombre o teléfono..."
                                    disabled={!currentUser?.businessId || loading || pageChanging}
                                />
                            </div>
                            {/* Orden de la lista (nombre / última visita / registro) */}
                            <select
                                value={sortBy}
                                onChange={handleSortChange}
                                disabled={!currentUser?.businessId || loading || pageChanging}
                                aria-label="Ordenar clientes"
                                title="Ordenar la lista"
                                className="px-3 py-2 text-sm font-medium rounded-md border border-line bg-surface text-content-2 hover:bg-surface-2 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            >
                                <option value="name">Nombre (A-Z)</option>
                                <option value="last_visit">Última visita</option>
                                <option value="recent">Registro (recientes)</option>
                            </select>
                            {/* v1.8.0: alternar activos / archivados */}
                            <button
                                onClick={toggleShowArchived}
                                disabled={loading || pageChanging}
                                title={showArchived ? 'Volver a clientes activos' : 'Ver clientes archivados'}
                                className={`inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 ${
                                    showArchived
                                        ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30'
                                        : 'text-content-2 bg-surface border-line hover:bg-surface-2'
                                }`}
                            >
                                {showArchived
                                    ? (<><ArchiveRestore className="h-4 w-4 mr-1.5" /> Ver activos</>)
                                    : (<><Archive className="h-4 w-4 mr-1.5" /> Ver archivados</>)}
                            </button>
                        </div>
                        {showArchived && (
                            <p className="mb-3 text-xs text-amber-700 dark:text-amber-300">
                                Mostrando clientes <strong>archivados</strong>. Abre uno para desarchivarlo.
                            </p>
                        )}

                        <div className="min-h-[300px]">
                            {/* Desktop Table View */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-line">
                                    <thead className="bg-surface-2">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Última Visita</th>
                                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">
                                                    {loyaltyMode === 'cashback' ? 'Saldo Actual' : 'Puntos'}
                                                </th>
                                                <th scope="col" className="px-3 py-3"><span className="sr-only">Registrar visita</span></th>
                                            </tr>
                                    </thead>
                                    <tbody className="bg-surface divide-y divide-line">
                                            {(loading && clients.length === 0) ? (
                                                Array.from({ length: ITEMS_PER_PAGE / 2 }).map((_, index) => (
                                                    <tr key={`skeleton-${index}`} className="animate-pulse">
                                                         <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-3/4 mb-2"></div><div className="h-3 bg-skeleton rounded w-1/2"></div></td>
                                                         <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-24"></div></td>
                                                         <td className="px-6 py-4 whitespace-nowrap text-center"><div className="h-4 bg-skeleton rounded w-12 mx-auto"></div></td>
                                                    </tr>
                                                ))
                                            ) : (!loading && !pageChanging && clients.length === 0) ? (
                                                <tr><td colSpan={4} className="px-6 py-12 text-center text-sm text-content-3">No hay clientes para mostrar.</td></tr>
                                            ) : (
                                                clients.map((client, index) => (
                                                    <tr key={client.id} className="hover:bg-surface-2 transition-colors duration-150 animate-fade-in cursor-pointer" style={{ animationDelay: `${0.05 + index * 0.01}s` }} onClick={() => navigate(`/dashboard/clients/${client.id}/history`)}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-2">
                                                                <ClientContactInfo clientId={client.id} clientName={client.visitorName} phoneNumber={client.phoneNumber} countryCode={client.countryCode} nameAsLink={false} />
                                                                <NoAcumulaChip block={noAcumula.get(String(client.id))} />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                                                             <div className="flex items-center">
                                                                 <CalendarClock className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0"/>
                                                                 <span>{formatDate(client.lastVisit)}</span>
                                                             </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${client.points > 0 ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-canvas text-content'}`}>
                                                                {loyaltyMode === 'cashback' ? (
                                                                    <>
                                                                        <DollarSign className="w-3 h-3 mr-0.5" />
                                                                        {client.points.toFixed(2)}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {Math.floor(client.points)} pts
                                                                    </>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                                            {/* Registrar visita (venta) — check-in real, sin entrar a la ficha */}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setVisitFor(client); }}
                                                                title="Registrar visita (venta)"
                                                                className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 hover:text-accent"
                                                            >
                                                                <Coins className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="md:hidden mt-4">
                                {(loading && clients.length === 0) ? (
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <div key={`skeleton-mobile-${index}`} className="animate-pulse mb-3 border rounded-lg p-3">
                                            <div className="flex justify-between items-start">
                                                <div className="w-3/4">
                                                    <div className="h-4 bg-skeleton rounded w-2/3 mb-2"></div>
                                                    <div className="h-3 bg-skeleton rounded w-1/2"></div>
                                                    <div className="h-3 bg-skeleton rounded w-full mt-2"></div>
                                                </div>
                                                <div className="h-5 w-10 bg-skeleton rounded-full"></div>
                                            </div>
                                            <div className="mt-2 pt-2 border-t border-line">
                                                <div className="h-3 bg-skeleton rounded w-1/2"></div>
                                            </div>
                                        </div>
                                    ))
                                ) : (!loading && !pageChanging && clients.length === 0) ? (
                                    <div className="py-8 text-center text-sm text-content-3">No hay clientes.</div>
                                ) : (
                                    <div className="space-y-3">
                                            {clients.map((client, index) => (
                                                <div key={client.id} className="border rounded-lg p-3 hover:bg-surface-2 transition-colors duration-150 animate-fade-in-up cursor-pointer" style={{animationDelay: `${0.1 + index * 0.05}s`}} onClick={() => navigate(`/dashboard/clients/${client.id}/history`)}>
                                                    <div className="flex justify-between items-start gap-2">
                                                        <div className="flex-grow">
                                                            <ClientContactInfo clientId={client.id} clientName={client.visitorName} phoneNumber={client.phoneNumber} countryCode={client.countryCode} iconSize="sm" nameAsLink={false} />
                                                            <NoAcumulaChip block={noAcumula.get(String(client.id))} className="mt-1" />
                                                        </div>
                                                        <div className="flex-shrink-0 ml-2 flex items-center gap-1.5">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setVisitFor(client); }}
                                                                title="Registrar visita (venta)"
                                                                className="rounded-lg border border-line p-1.5 text-content-2 hover:text-accent"
                                                            >
                                                                <Coins className="h-4 w-4" />
                                                            </button>
                                                            <span className={`px-2 py-0.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${client.points > 0 ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-canvas text-content'}`}>
                                                                {loyaltyMode === 'cashback' ? (
                                                                    <>
                                                                        <DollarSign className="w-3 h-3 mr-0.5" />
                                                                        {client.points.toFixed(2)}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {Math.floor(client.points)} pts
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 pt-2 border-t border-line flex justify-start items-center text-xs text-content-3">
                                                         <CalendarClock className="h-3.5 w-3.5 mr-1 text-content-3 flex-shrink-0"/>
                                                         <span>Última visita: {formatDate(client.lastVisit)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Pagination Controls */}
                        {totalHits > ITEMS_PER_PAGE && (
                            <PaginationControls
                                currentPage={currentPageNum}
                                isLoading={loading || pageChanging}
                                onPrevPage={handlePrevPage}
                                onNextPage={handleNextPage}
                                canGoPrev={canGoPrev && !pageChanging}
                                canGoNext={canGoNext && !pageChanging}
                                itemsPerPage={ITEMS_PER_PAGE}
                                countOnPage={clients.length}
                                totalItems={totalHits}
                            />
                        )}
                        {(!loading && !pageChanging && clients.length === 0 && totalHits === 0) && (
                            <div className="mt-4 px-4 py-3 text-center text-sm text-content-3 border-t border-line sm:px-6">
                                No hay clientes registrados para mostrar.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {pageChanging && (
                <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
                    <span className="text-sm font-medium text-content-2">Cambiando página...</span>
                </div>
            )}

            {/* Modal de selección de campos para exportar */}
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={exportToExcel}
                fields={exportFields}
                title="Exportar Clientes"
                description="Selecciona los campos que deseas incluir en el reporte Excel."
                totalRecords={totalHits}
                isExporting={isExporting}
            />

            {/* Registrar visita (venta) desde la lista — check-in real; refresca el saldo local */}
            {visitFor && (
                <RegisterVisitModal
                    client={{ id: String(visitFor.id), name: visitFor.visitorName, phone: visitFor.phoneNumber, tags: visitFor.tags }}
                    onClose={() => setVisitFor(null)}
                    onDone={({ balance }) => {
                        setClients(prev => prev.map(c => c.id === visitFor.id ? { ...c, points: balance, lastVisit: new Date() } : c));
                    }}
                />
            )}
        </Layout>
    );
};

export default ClientsListPage;